import { type AxiosInstance, type AxiosResponse } from 'axios';
import { type DatafeedConfiguration, type LibrarySymbolInfo, type ResolutionString } from 'charting_library';

import { SystemError } from '#/shared/exceptions/system-error';
import { dateHeaderToTimestamp } from '#/shared/utils/date-header-to-timestamp';

import { type HmrSchedule } from '#/types/hmr.types';
import { type CandlesUdfResponse, type TicksUdfResponse } from '#/types/udf.types';

import { type HistoryUrlParams, type IApiProvider, type SymbolsUrlParams } from '../api-provider.types';

import { MICROSECONDS_IN_SECOND } from './crypto.model';
import { type CryptoBars, type CryptoConfig, type CryptoSymbolInfo } from './crypto.types';

export class CryptoProvider implements IApiProvider {
  readonly #providerName = 'CryptoProvider';

  readonly #client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.#client = client;
  }

  #configResponse?: Promise<AxiosResponse<CryptoConfig>>;

  // ? serverTime() and config() both read /v1/config; caching the in-flight request means one call answers both,
  // ? whichever of them runs first. `??=` only skips a null/undefined field, and a rejected promise is neither,
  // ? so a failed request clears the cache itself — otherwise every later call would replay that one rejection
  // ? for the rest of the session instead of trying again.
  #fetchConfig() {
    this.#configResponse ??= this.#client
      .get<never, AxiosResponse<CryptoConfig>>('/v1/config')
      .catch((error: unknown) => {
        this.#configResponse = undefined;

        throw error;
      });

    return this.#configResponse;
  }

  async serverTime() {
    const response = await this.#fetchConfig();

    return dateHeaderToTimestamp(response.headers as Record<string, string | undefined>, this.#providerName);
  }

  async config(): Promise<AxiosResponse<DatafeedConfiguration>> {
    const response = await this.#fetchConfig();

    const { supported_resolutions, supports_marks, symbol_types } = response.data;

    // ? `supports_group_request` and `supports_search` steer a stock UDF adapter; this datafeed is our own.
    return {
      ...response,
      data: {
        supported_resolutions: supported_resolutions as ResolutionString[],
        supports_marks,
        supports_time: true,
        symbols_types: symbol_types,
      },
    };
  }

  async symbols({ urlParams }: { urlParams: SymbolsUrlParams }): Promise<AxiosResponse<LibrarySymbolInfo | null>> {
    const response = await this.#client.get<never, AxiosResponse<CryptoSymbolInfo | null>>('/v1/symbols', {
      params: urlParams,
    });

    // ? A delisted symbol answers 200 with a null body, same as the legacy feed.
    if (!response.data) return { ...response, data: null };

    return { ...response, data: this.#toLibrarySymbolInfo(response.data) };
  }

  async candlesHistory({ urlParams }: { urlParams: HistoryUrlParams }): Promise<AxiosResponse<CandlesUdfResponse>> {
    // ? `source` picks an MT5 server; the venue has no equivalent, so it's the one field left out below.
    const { symbol, resolution, from, to, countback } = urlParams;

    const response = await this.#client.get<never, AxiosResponse<CryptoBars>>('/v1/history', {
      params: {
        symbol,
        resolution,
        // ? The window arrives in the seconds the UDF candles endpoint reads; this feed bounds in microseconds.
        from: from === undefined ? undefined : from * MICROSECONDS_IN_SECOND,
        to: to === undefined ? undefined : to * MICROSECONDS_IN_SECOND,
        countback,
      },
    });

    return { ...response, data: this.#toCandlesUdfResponse(response.data) };
  }

  async ticksHistory(): Promise<AxiosResponse<TicksUdfResponse>> {
    // ? The venue has no ticks history; `no_data` is the UDF shape every caller already treats as empty.
    return { data: { s: 'no_data' } } as AxiosResponse<TicksUdfResponse>;
  }

  async hmrScheduleHistory(): Promise<AxiosResponse<HmrSchedule[]>> {
    const data: HmrSchedule[] = [];

    return { data } as AxiosResponse<HmrSchedule[]>;
  }

  #toLibrarySymbolInfo(symbolInfo: CryptoSymbolInfo): LibrarySymbolInfo {
    return {
      name: symbolInfo.name,
      ticker: symbolInfo.ticker,
      description: symbolInfo.description,
      type: symbolInfo.type,
      exchange: symbolInfo.exchange,
      listed_exchange: symbolInfo.listed_exchange,
      session: symbolInfo.session,
      timezone: symbolInfo.timezone as LibrarySymbolInfo['timezone'],
      format: symbolInfo.format as LibrarySymbolInfo['format'],
      pricescale: symbolInfo.pricescale,
      minmov: symbolInfo.minmovement,
      minmove2: symbolInfo.minmovement2,
      has_daily: symbolInfo.has_daily,
      has_intraday: symbolInfo.has_intraday,
      has_seconds: symbolInfo.has_seconds,
      has_ticks: symbolInfo.has_ticks,
      has_weekly_and_monthly: symbolInfo.has_weekly_and_monthly,
      // ? Base-asset volumes move by the order step, and the library renders whole numbers without this.
      volume_precision: this.#toVolumePrecision(symbolInfo.stepSize),
    };
  }

  #toVolumePrecision(stepSize: string | undefined) {
    // ? Cosmetic, so a feed that stops sending the step falls back to whole units instead of failing the symbol.
    const [, decimals = ''] = (stepSize ?? '').split('.');

    return decimals.replace(/0+$/, '').length;
  }

  #toCandlesUdfResponse(barsPage: CryptoBars): CandlesUdfResponse {
    // ? The feed sends no `nextTime`, and the charting library treats it as optional metadata.
    if (barsPage.s === 'no_data') return { s: 'no_data' };

    // ? Beyond what the generated type promises: a malformed 200 can answer neither shape.
    if (barsPage.s !== 'ok') {
      throw new SystemError('History bars page carries a status that is neither "ok" nor "no_data".', {
        tags: { provider: this.#providerName, status: barsPage.s },
      });
    }

    const { t, o, h, l, c, v } = barsPage;

    // ? Every column is read below, so a missing one has to fail here rather than as a TypeError inside a map.
    const missingColumn = Object.entries({ t, o, h, l, c, v }).find(([, column]) => !Array.isArray(column));

    if (missingColumn) {
      throw new SystemError('History bars page is missing a column.', {
        tags: { provider: this.#providerName, column: missingColumn[0] },
      });
    }

    return {
      s: 'ok',
      // ? Bucket open times are epoch microseconds; a UDF candles page carries seconds.
      t: t.map((time) => time / MICROSECONDS_IN_SECOND),
      o: o.map((value, index) => this.#toNumber(value, 'o', index)),
      h: h.map((value, index) => this.#toNumber(value, 'h', index)),
      l: l.map((value, index) => this.#toNumber(value, 'l', index)),
      c: c.map((value, index) => this.#toNumber(value, 'c', index)),
      // ? The base-asset volume, the one a crypto chart plots; `qv` is the same trade in quote terms.
      v: v.map((value, index) => this.#toNumber(value, 'v', index)),
    };
  }

  #toNumber(value: string, column: string, index: number) {
    // ? Number('') and Number(' ') parse to 0 rather than NaN; treat a blank value as invalid, not a real zero.
    const parsed = value.trim() === '' ? NaN : Number(value);

    if (!Number.isFinite(parsed)) {
      throw new SystemError('History bars carry a value that is not a number.', {
        tags: { provider: this.#providerName },
        column,
        index,
        value,
      });
    }

    return parsed;
  }
}
